import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { useNotifications } from '../context/NotificationContext'
import { useTheme } from '../context/ThemeContext'
import ModelSelector from './ModelSelector'
import type { Model } from '../data/models'
import HeaderActions from './HeaderActions'
import { useAccountNavigation } from '../hooks/useAccountNavigation.ts'
import ThemeToggle from './ThemeToggle.tsx'
import { useSelectedWelcomeIntent } from '../hooks/useWelcomeIntent'
import { backgroundOf } from '../utils/routeModal'

interface Props {
	onMenuClick: () => void
	selectedModel: Model | null
	onSelectModel: (model: Model | null) => void
	isSidebarOpen: boolean
}

export default function Header({
	onMenuClick,
	selectedModel,
	onSelectModel,
	isSidebarOpen,
}: Props) {
	const { t } = useTranslation()
	const { user } = useAuth()
	const navigate = useNavigate()
	const { theme } = useTheme()
	const location = useLocation()
	// Под окном входа и тарифов шапка остаётся шапкой страницы под окном, а не адреса окна (/register, /tariffs)
	const page = backgroundOf(location) ?? location
	const isGenerate = page.pathname === '/generate' || page.pathname.startsWith('/chat')
	const isHome = page.pathname === '/'
	const isHomeRoute = isHome || page.pathname === '/main_temp'
	const handleAccountClick = useAccountNavigation()
	// На телефоне колокольчик шапки скрыт, он живёт в меню: точка на кнопке меню ведёт к непрочитанным
	// (владелец 24.10, «да делай точку»). Дальше по пути — аватар внизу меню и колокольчик (SidebarQuickMenu).
	const { unreadCount } = useNotifications()
	const hasUnread = !!user && unreadCount > 0

	// Welcome-intent: на главной показываем выбранную модель в ModelSelector
	// ТОЛЬКО после клика юзера по welcome-плитке (есть selected intent в
	// sessionStorage). До клика — placeholder "Все нейросети", чтобы
	// возвращение на главную не вешало предыдущий выбор из /chat.
	//
	// Lightweight-хук: sessionStorage + same-tab event listener. Не делает
	// fetch к /welcome-config (тот висит на HomeTemp через useWelcomeIntent).
	const welcomeIntent = useSelectedWelcomeIntent()
	const showSelectedInHeader = isGenerate || (isHomeRoute && !!welcomeIntent)
	const headerSelected = showSelectedInHeader ? selectedModel : null

	const handleSelectModel = (model: Model) => {
		onSelectModel(model)
		if (model.category === 'text') {
			if (!location.pathname.startsWith('/chat')) {
				navigate('/chat')
			}
		} else {
			if (!location.pathname.startsWith('/generate')) {
				navigate('/generate')
			}
		}
	}
	return (
		<header className={`header ${isSidebarOpen ? 'sidebar-open' : ''}`}>
			<div className="header-top">
				<div className={`header-left ${isSidebarOpen ? 'is-hidden-during-sidebar' : ''}`}>
					<div className={`header-logo-mobile${isHome ? ' is-home' : ''}`} onClick={() => navigate('/')}>
						<img src="/icons/logo.svg" alt="" className="header-logo-icon" />
					</div>
					<div className="header-model-selector">
						<ModelSelector
							// key re-mount чтобы ModelSelector сбрасывал internal
							// state (открытый dropdown, фильтры) при переключении
							// между «выбрано» / «все нейросети» режимами.
							key={headerSelected ? 'model' : 'all'}
							selected={headerSelected}
							onSelect={handleSelectModel}
							placeholder={headerSelected ? undefined : t('nav.allModels')}
						/>
					</div>
				</div>
				<div className="header-right">
					<ThemeToggle />
					<HeaderActions user={user} theme={theme} onAccountClick={handleAccountClick} />
					<button className="header-menu-btn" onClick={onMenuClick} aria-label={hasUnread ? t('nav.openMenuUnread') : t('nav.openMenu')}>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
						>
							<path d="M4 6h16M4 12h16M4 18h16" />
						</svg>
						{hasUnread && <span className="notification-dot" aria-hidden="true" />}
					</button>
				</div>
			</div>
		</header>
	)
}
